import { z } from "zod";
import { reportUiFailure } from "./report-failure.js";
import type { LogsSecurityEvent, LogsSecurityEventsPage } from "./logs/timeline/wire.js";

const deviceSessionSchema = z.object({
  id: z.string().min(1).max(256),
  deviceName: z.string().max(200),
  countryCode: z.string().max(80).nullable(),
  region: z.string().max(200).nullable(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  current: z.boolean(),
  // S3 fields: only a recorded caller gets them, so an older relay omits them.
  city: z.string().max(200).nullable().optional(),
  country: z.string().max(80).nullable().optional(),
  asnOrg: z.string().max(200).nullable().optional(),
  clientKind: z.string().max(40).nullable().optional(),
  // "qr_pair" for a phone connected from Settings → Connect iPhone (plan C6). Older relays omit it.
  loginMethod: z.string().max(40).nullable().optional(),
  appVersion: z.string().max(80).nullable().optional(),
  os: z.string().max(80).nullable().optional(),
  browser: z.string().max(80).nullable().optional(),
  // Masked by the relay ("73.21.xx.xx"). Anything unmasked is dropped, never shown.
  maskedIp: z
    .string()
    .max(64)
    .nullable()
    .optional()
    .transform((v) => (typeof v === "string" && v.includes("x") ? v : null)), // null: wire null for an unmasked/absent address
  // city/region/country/asnOrg place the masked IP (last seen); this is the sign-in network,
  // null when it is the same one. Older relays omit it.
  signedInFrom: z
    .object({
      city: z.string().max(200).nullable(),
      region: z.string().max(200).nullable(),
      country: z.string().max(80).nullable(),
      asnOrg: z.string().max(200).nullable(),
    })
    .nullable()
    .optional(),
});
/** The relay lists at most this many sessions, most recent first. */
export const ACCOUNT_SESSIONS_LIMIT = 100;
const accountListSchema = z.object({
  sessions: z.array(deviceSessionSchema).max(ACCOUNT_SESSIONS_LIMIT),
  signOutOthers: z.boolean().optional(),
});
export type DeviceSession = z.infer<typeof deviceSessionSchema>;

export interface DeviceSessionsApi {
  revoke(sessionId: string): Promise<void>;
  revokeCurrent(): Promise<void>;
}

export interface AccountSessions {
  sessions: DeviceSession[];
  /** The relay can sign out the other sessions (POST revoke-others). Absent = false. */
  signOutOthers: boolean;
}

export interface SecurityEventsQuery {
  since: number;
  until?: number;
  cursor?: string;
  limit?: number;
}

export interface AccountSessionsApi extends DeviceSessionsApi {
  listAccount(): Promise<AccountSessions>;
  revokeOthers(): Promise<{ revoked: number }>;
  /** The full address of one session, or null once retention has cut it. */
  reveal(sessionId: string): Promise<string | null>;
  securityEvents(query: SecurityEventsQuery): Promise<LogsSecurityEventsPage>;
  requestSecurityLink(eventId: string): Promise<void>;
  unreadSecurityEvents(since: number): Promise<LogsSecurityEvent[]>;
  securityConfirmation(eventId: string): Promise<SecurityConfirmation | undefined>;
  securityVerdict(eventId: string, verdict: SecurityVerdict, stepUp?: SecurityStepUp): Promise<SecurityVerdictAnswer>; // prettier-ignore
}

const HTTP_CODES: Partial<Record<number, string>> = {
  400: "invalid_request",
  401: "unauthorized",
  403: "forbidden",
  404: "not_found",
  409: "session_not_listed",
  429: "rate_limited",
};

/** Why the relay refused "It was me" from this session (relay-only/login-guard/confirmation.ts). */
export const CONFIRM_REFUSALS = [
  "no_session",
  "own_session",
  "newer_session",
  "related_session",
  "own_unanswered_signin",
  "own_unanswered_refresh",
  "untrusted_session",
  "answered",
] as const;
export type ConfirmRefusal = (typeof CONFIRM_REFUSALS)[number];
const refusalSchema = z.enum(CONFIRM_REFUSALS);

export class DeviceSessionHttpError extends Error {
  readonly code: string;
  /** 429 only: how long the relay asked to wait. */
  constructor(
    readonly status: number,
    readonly retryAfterMs?: number,
    readonly stepUpAvailable = false,
    /** 403 "cannot_self_confirm" only, when the relay names it; a reason this client doesn't know is undefined. */
    readonly reason?: ConfirmRefusal,
  ) {
    super(
      status === 401
        ? "Sign in again to manage devices."
        : "Could not update your devices. Try again.",
    );
    this.name = "DeviceSessionHttpError";
    this.code = HTTP_CODES[status] ?? "unavailable";
  }
}

async function retryAfterOf(response: Response): Promise<number | undefined> {
  if (response.status !== 429) return undefined;
  try {
    const body = (await response.json()) as { retryAfterMs?: unknown };
    return typeof body.retryAfterMs === "number" && body.retryAfterMs > 0
      ? body.retryAfterMs
      : undefined;
  } catch (err) {
    reportUiFailure("settings.sessions.retry_after", err);
    return undefined;
  }
}

export async function request(
  base: string,
  token: string,
  path: string,
  method = "GET",
  body: unknown = {},
  keepalive = false,
): Promise<unknown> {
  const response = await fetch(`${base}${path}`, {
    method,
    cache: "no-store",
    ...(keepalive ? { keepalive: true } : {}),
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    ...(method === "POST" ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    let stepUpAvailable = false;
    let reason: ConfirmRefusal | undefined;
    if (response.status === 403) {
      const refusal: unknown = await response
        .clone()
        .json()
        .catch((err: unknown) => {
          reportUiFailure("settings.sessions.refusal", err);
          return {};
        });
      const parsed = z.object({ stepUpAvailable: z.boolean().optional() }).safeParse(refusal);
      stepUpAvailable = parsed.success && parsed.data.stepUpAvailable === true;
      const named = z.object({ reason: refusalSchema }).safeParse(refusal);
      reason = named.success ? named.data.reason : undefined; // undefined: unnamed or unknown to this client
    }
    throw new DeviceSessionHttpError(
      response.status,
      await retryAfterOf(response),
      stepUpAvailable,
      reason,
    );
  }
  try {
    return await response.json();
  } catch {
    throw new Error("Invalid device response");
  }
}

export async function listAccountSessions(base: string, token: string): Promise<AccountSessions> {
  const parsed = accountListSchema.safeParse(await request(base, token, "/api/auth/sessions"));
  if (!parsed.success) throw new Error("Invalid device list response");
  return { sessions: parsed.data.sessions, signOutOthers: parsed.data.signOutOthers === true };
}

/** POST, never the everywhere-DELETE: an old relay answers 404 and the caller says so. */
export async function revokeOtherDeviceSessions(
  base: string,
  token: string,
): Promise<{ revoked: number }> {
  const parsed = z
    .object({ ok: z.literal(true), revoked: z.number().int().min(0) })
    .safeParse(await request(base, token, "/api/auth/sessions/revoke-others", "POST"));
  if (!parsed.success) throw new Error("Invalid device revocation response");
  return { revoked: parsed.data.revoked };
}

/** GET one session's full address. A parse failure never echoes the value. */
export async function revealDeviceSessionIp(
  base: string,
  token: string,
  sessionId: string,
): Promise<string | null> {
  const parsed = z
    .object({ ip: z.string().min(1).max(64).nullable() })
    .safeParse(
      await request(base, token, `/api/auth/sessions/${encodeURIComponent(sessionId)}/ip`),
    );
  if (!parsed.success) throw new Error("Invalid IP response");
  return parsed.data.ip;
}

const securityEventSchema = z.object({
  id: z.string().min(1).max(256),
  kind: z.string().min(1).max(64),
  at: z.number().int().nonnegative(),
  sessionId: z.string().max(256).nullable(),
  country: z.string().max(80).nullable(),
  asnOrg: z.string().max(200).nullable(),
  clientKind: z.string().max(40).nullable(),
  os: z.string().max(80).nullable(),
  browser: z.string().max(80).nullable(),
  decision: z.string().max(40).nullable(),
  userVerdict: z.string().max(40).nullable(),
  linkExpiresAt: z.number().finite().nullable().optional(),
});
const securityPageSchema = z.object({
  events: z.array(securityEventSchema).max(100),
  nextCursor: z.string().max(300).nullable().optional(),
});

/** zod strips unknown keys, so an `ip` or `city` the relay should not send never reaches a card. */
export async function listSecurityEvents(
  base: string,
  token: string,
  query: SecurityEventsQuery,
): Promise<LogsSecurityEventsPage> {
  const params = new URLSearchParams({ since: String(query.since) });
  if (query.until !== undefined) params.set("until", String(query.until));
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const parsed = securityPageSchema.safeParse(
    await request(base, token, `/api/auth/security-events?${params}`),
  );
  if (!parsed.success) throw new Error("Invalid security events response");
  return { events: parsed.data.events, nextCursor: parsed.data.nextCursor ?? null }; // null: wire cursor is null at the last page
}

// The banner's own read: flagged and unanswered only, the one place a city is kept (VP Q1).
// Display text is cut to its cap, never rejected: one long GeoIP name must not hide every flag
// (S1). Ids, kinds and decisions stay strict.
const capped = (max: number) => z.string().transform((v) => [...v].slice(0, max).join(""));
const unreadPageSchema = z.object({
  events: z
    .array(
      securityEventSchema.extend({
        country: capped(80).nullable(),
        asnOrg: capped(200).nullable(),
        clientKind: capped(40).nullable(),
        os: capped(80).nullable(),
        browser: capped(80).nullable(),
        city: capped(200).nullable().optional(),
      }),
    )
    .max(100),
});

/** GET unread=1 (the P2 filter): the reader's own flagged, unanswered sign-ins, newest first. */
export async function listUnreadSecurityEvents(
  base: string,
  token: string,
  since: number,
): Promise<LogsSecurityEvent[]> {
  const params = new URLSearchParams({ since: String(since), unread: "1", limit: "100" });
  const parsed = unreadPageSchema.safeParse(
    await request(base, token, `/api/auth/security-events?${params}`),
  );
  if (!parsed.success) throw new Error("Invalid security events response");
  return parsed.data.events;
}

/** May THIS session answer "It was me" for one event (the relay's own decision, read ahead). */
export type SecurityConfirmation =
  | { canConfirm: true }
  | { canConfirm: false; reason?: ConfirmRefusal; stepUpAvailable: boolean };
const confirmationSchema = z.union([
  z.object({ canConfirm: z.literal(true) }),
  z.object({
    canConfirm: z.literal(false),
    reason: z.string(),
    stepUpAvailable: z.boolean().optional(),
  }),
]);

/** undefined: a relay older than the read-ahead (404); the banner then keeps its own guess. */
export async function getSecurityConfirmation(
  base: string,
  token: string,
  eventId: string,
): Promise<SecurityConfirmation | undefined> {
  const path = `/api/auth/security-events/${encodeURIComponent(eventId)}/confirmation`;
  let body: unknown;
  try {
    body = await request(base, token, path);
  } catch (err) {
    if (err instanceof DeviceSessionHttpError && err.status === 404) return undefined;
    throw err;
  }
  const parsed = confirmationSchema.safeParse(body);
  if (!parsed.success) throw new Error("Invalid security confirmation response");
  if (parsed.data.canConfirm) return { canConfirm: true };
  const reason = refusalSchema.safeParse(parsed.data.reason);
  return {
    canConfirm: false,
    ...(reason.success ? { reason: reason.data } : {}),
    stepUpAvailable: parsed.data.stepUpAvailable === true,
  };
}

export type SecurityVerdict = "me" | "not_me";
export type SecurityStepUp = { request: true } | { code: string };
export interface SecurityVerdictAnswer {
  complete: boolean;
  message?: string;
  stepUpSent?: boolean;
}
const verdictSchema = z.object({
  ok: z.literal(true),
  complete: z.boolean(),
  stepUpSent: z.boolean().optional(),
  message: z.string().max(4000).optional(),
});

/** P2's in-app answer. 403 cannot_self_confirm, 409 already_answered, 429 surface as the status. */
export async function postSecurityVerdict(
  base: string,
  token: string,
  eventId: string,
  verdict: SecurityVerdict,
  stepUp?: SecurityStepUp,
): Promise<SecurityVerdictAnswer> {
  const path = `/api/auth/security-events/${encodeURIComponent(eventId)}/verdict`;
  const parsed = verdictSchema.safeParse(
    await request(base, token, path, "POST", { verdict, ...(stepUp ? { stepUp } : {}) }),
  );
  if (!parsed.success) throw new Error("Invalid verdict response");
  return {
    complete: parsed.data.complete,
    message: parsed.data.message,
    ...(parsed.data.stepUpSent ? { stepUpSent: true } : {}),
  };
}

export async function revokeDeviceSession(
  base: string,
  token: string,
  sessionId?: string,
): Promise<void> {
  const path = sessionId
    ? `/api/auth/sessions/${encodeURIComponent(sessionId)}`
    : "/api/auth/revoke-pass";
  const parsed = z
    .object({ ok: z.literal(true) })
    // keepalive on "this device": Sign out may close the window right after the click.
    .safeParse(await request(base, token, path, sessionId ? "DELETE" : "POST", {}, !sessionId));
  if (!parsed.success) throw new Error("Invalid device revocation response");
}

interface DeviceSessionClient {
  revokeDeviceSession(id: string): Promise<void>;
  revokeCurrentDeviceSession(): Promise<void>;
  listAccountSessions(): Promise<AccountSessions>;
  revokeOtherDeviceSessions(): Promise<{ revoked: number }>;
  revealDeviceSessionIp(sessionId: string): Promise<string | null>;
  listSecurityEvents(query: SecurityEventsQuery): Promise<LogsSecurityEventsPage>;
  relayHttp<T>(run: (base: string, token: string) => Promise<T>): Promise<T>;
  refreshAuthToken(): Promise<string>;
}

/** Runs one relay HTTP call for the current connection, refreshing once on 401/409. */
export function connectionCaller(
  client: Pick<DeviceSessionClient, "refreshAuthToken">,
  isCurrent: () => boolean,
): <T>(action: () => Promise<T>) => Promise<T> {
  function assertCurrent(): void {
    if (!isCurrent()) throw new DeviceSessionHttpError(401);
  }
  async function perform<T>(action: () => Promise<T>): Promise<T> {
    assertCurrent();
    try {
      const result = await action();
      assertCurrent();
      return result;
    } catch (err) {
      assertCurrent();
      // 401 (expired access) and 409 session_not_listed (a pre-ledger pass) are both cured by
      // one refresh; the retry runs once, so a 409 then 401 cannot refresh twice.
      if (!(err instanceof DeviceSessionHttpError) || (err.status !== 401 && err.status !== 409))
        throw err;
      const outcome = await client.refreshAuthToken();
      assertCurrent();
      if (outcome !== "refreshed") throw err;
      const result = await action();
      assertCurrent();
      return result;
    }
  }
  return perform;
}

export function deviceSessionsForConnection(
  client: DeviceSessionClient,
  isCurrent: () => boolean,
): AccountSessionsApi {
  const perform = connectionCaller(client, isCurrent);
  return {
    revoke: (id) => perform(() => client.revokeDeviceSession(id)),
    revokeCurrent: () => perform(() => client.revokeCurrentDeviceSession()),
    listAccount: () => perform(() => client.listAccountSessions()),
    revokeOthers: () => perform(() => client.revokeOtherDeviceSessions()),
    reveal: (id) => perform(() => client.revealDeviceSessionIp(id)),
    securityEvents: (query) => perform(() => client.listSecurityEvents(query)),
    requestSecurityLink: (eventId) =>
      perform(() =>
        client.relayHttp(async (base, token) => {
          const result = await request(
            base,
            token,
            `/api/auth/security-events/${encodeURIComponent(eventId)}/link`,
            "POST",
          );
          if (!z.object({ ok: z.literal(true) }).safeParse(result).success)
            throw new Error("Invalid security link response");
        }),
      ),
    unreadSecurityEvents: (since) =>
      perform(() => client.relayHttp((b, t) => listUnreadSecurityEvents(b, t, since))),
    securityConfirmation: (id) =>
      perform(() => client.relayHttp((b, t) => getSecurityConfirmation(b, t, id))),
    securityVerdict: (id, verdict, stepUp) =>
      perform(() => client.relayHttp((b, t) => postSecurityVerdict(b, t, id, verdict, stepUp))),
  };
}
